import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { areEqual } from 'Utils/equalityChecks';

import { categoriesSelector, projectSelector, propertySelector } from 'Containers/RocketScan/selectors';

import { Galleries, RoomNotes } from 'Containers/RocketScan';
import { listRoomImageProcessors } from 'Containers/RocketScan/MultiUnit/RoomTiles/RoomTile/actions';
import { NoPhotoPlaceholder, DamagedMaterialsWrapper } from 'Components/RocketScan';
import { SecondarySpinner } from 'Components/SecondarySpinner';
import { useRoomFunctions } from 'Context/RocketScan';
import { pusherSelector } from 'Containers/Core/selectors';
import {
  getRoomDamageMaterials,
  getRoomScopeOfWorkItems,
} from 'Containers/RocketScan/RoomsView/DamagedMaterials/actions';
import { DamageMaterial, SelectedItem } from 'Containers/RocketScope/types';
import { userFeatureFlagsSelector } from 'Containers/User/selector';
import { locationSelector } from 'Containers/RocketScan/MultiUnit/Locations/selectors';

interface Props {
  room: any;
  editable: boolean;
  hasBookmarked?: boolean;
  hasFlagged?: boolean;
  setAlbumPhotos: (e: any) => void;
  onEditButtonClick: (e: any) => void;
}

const RoomContentBodyContainer = ({
  room,
  editable,
  hasBookmarked,
  hasFlagged,
  setAlbumPhotos,
  onEditButtonClick,
}: Props) => {
  const { rocketScope, imageProcessor } = useSelector(userFeatureFlagsSelector, areEqual);

  const { id: roomId, photo_albums: photoAlbums, notes } = room;

  const dispatch = useDispatch();

  const mounted = useRef(false);
  const ref = useRef(null);

  const {
    imageProcessorCheckToken,
    triggerImageProcessorRefresh,
    latestAssemblyId,
    locallyEnabledAlbumIds,
    clearLocallyEnabledAlbums,
  }: any = useRoomFunctions();

  const property = useSelector(propertySelector, areEqual);
  const pusher = useSelector(pusherSelector, areEqual);
  const notesCategories = useSelector(categoriesSelector, areEqual);
  const project = useSelector(projectSelector, areEqual);
  const location = useSelector(locationSelector, areEqual);
  const [roomDamagedMaterials, setRoomDamagedMaterials] = useState<DamageMaterial[] | undefined>(undefined);
  const [roomScopeOfWorkItems, setRoomScopeOfWorkItems] = useState<SelectedItem[] | undefined>(undefined);
  const [imageProcessorChannels, setImageProcessorChannels] = useState<string[]>([]);
  const [showImageProcessorSpinner, setShowImageProcessorSpinner] = useState(false);
  const [imageProcessorCounts, setImageProcessorCounts] = useState({ totalFiles: 0, completedFiles: 0, remainingFiles: 0 });

  let roomNotes = [];
  let damageNotes = [];
  if (notes) {
    roomNotes = notes.filter((note) => note.category.id === notesCategories.photo);
    damageNotes = notes.filter((note) => note.category.id === notesCategories.damage);
  }

  const mergedPhotoAlbums = useMemo(() => {
    const enabledIds = new Set(locallyEnabledAlbumIds);
    const merged = (photoAlbums || []).map((album: any) =>
      enabledIds.has(album.id) ? { ...album, enabled: 1 } : album
    );

    enabledIds.forEach((id) => {
      if (!merged.some((album: any) => album.id === id)) {
        merged.push({ id, enabled: 1 });
      }
    });

    return merged;
  }, [photoAlbums, locallyEnabledAlbumIds]);

  // reset locally enabled albums when switching rooms
  useEffect(() => {
    clearLocallyEnabledAlbums?.();
  }, [roomId]);

  const checkImageProcessorPending = useCallback(async () => {
    if (!imageProcessor) return;

    const response: any = await dispatch(listRoomImageProcessors(roomId));

    if (response?.data && mounted.current) {
      const { data } = response;
      const processingAssemblies = data.filter((processor: any) =>
        processor.status === 'processing' || processor.status === 'pending'
      );

      // Calculate total file counts across all assemblies
      const totalFiles = data.reduce((sum: number, processor: any) => sum + (processor.total_files || 0), 0);
      const completedFiles = data.reduce((sum: number, processor: any) => sum + (processor.completed_files || 0), 0);
      const remainingFiles = data.reduce((sum: number, processor: any) => sum + (processor.remaining_files || 0), 0);

      console.log('[ImageProcessor RoomContentBody] Assemblies fetched', {
        total: data.length,
        processing: processingAssemblies.length,
        counts: { totalFiles, completedFiles, remainingFiles },
      });

      // Update counts
      setImageProcessorCounts({
        totalFiles,
        completedFiles,
        remainingFiles
      });

      if (processingAssemblies.length > 0) {
        setShowImageProcessorSpinner(true);
        console.log('[ImageProcessor RoomContentBody] Showing spinner for active assemblies');

        // Set up Pusher subscriptions for each assembly
        const channels = processingAssemblies.map((processor: any) =>
          `imageprocessornotification.AssemblyId.${processor.assembly_id}`
        );
        setImageProcessorChannels(channels);
        console.log('[ImageProcessor RoomContentBody] Processing assemblies active:', channels);
      } else {
        setShowImageProcessorSpinner(false);
        setImageProcessorChannels([]);
        console.log('[ImageProcessor RoomContentBody] No active assemblies, hiding spinner');
      }
    }
  }, [roomId, mounted, imageProcessor, dispatch, pusher]);

  // initial API to check assembly status
  useEffect(() => {
    mounted.current = true;
    if (mounted.current) {
      (async () => {
        await checkImageProcessorPending();
      })();
    }

    return () => {
      mounted.current = false;
    };
  }, [checkImageProcessorPending]);

  // Trigger image processor check when uploads are initiated for this room
  useEffect(() => {
    if (!imageProcessor) return;
    if (!imageProcessorCheckToken) return;

    console.log('[ImageProcessor RoomContentBody] Triggered image processor check', {
      token: imageProcessorCheckToken,
      roomId,
    });

    (async () => {
      await checkImageProcessorPending();
    })();
  }, [imageProcessorCheckToken, imageProcessor, checkImageProcessorPending]);

  // Immediately subscribe when a new assembly id is registered (post-creation)
  useEffect(() => {
    if (!imageProcessor) return;
    if (!latestAssemblyId) return;

    const channelName = `imageprocessornotification.AssemblyId.${latestAssemblyId}`;
    setImageProcessorChannels((prev) => {
      if (prev.includes(channelName)) {
        return prev;
      }
      console.log('[ImageProcessor RoomContentBody] Adding channel from creation flow', channelName);
      return [...prev, channelName];
    });
    setShowImageProcessorSpinner(true);
  }, [imageProcessor, latestAssemblyId]);

  // Fallback polling while spinner is shown (in case Pusher events are missed)
  useEffect(() => {
    if (!imageProcessor) return;
    if (!showImageProcessorSpinner) return;

    const intervalId = window.setInterval(() => {
      console.log('[ImageProcessor RoomContentBody] Polling image processor status while spinner active');
      checkImageProcessorPending();
    }, 10000);

    return () => window.clearInterval(intervalId);
  }, [imageProcessor, showImageProcessorSpinner, checkImageProcessorPending]);

  // Subscribe to ImageProcessor Pusher events
  useEffect(() => {
    if (!imageProcessor || imageProcessorChannels.length === 0) {
      return;
    }

    const connection = pusher?.connection;
    const onConnectionStateChange = (states: any) => {
      console.log('[ImageProcessor RoomContentBody] Pusher connection state', states);
    };
    connection?.bind('state_change', onConnectionStateChange);

    const subscriptions = imageProcessorChannels.map((channelName) => {
      const subscription = pusher.subscribe(channelName);
      console.log('[ImageProcessor RoomContentBody] Subscribed to channel', channelName);

      subscription.bind('App\\Events\\ImageProcessorUpdated', (data: any) => {
        console.log('[ImageProcessor RoomContentBody] ImageProcessorUpdated event received:', data);

        if (data.roomId === roomId && mounted.current) {
          // Update counts using payload data
          setImageProcessorCounts({
            totalFiles: data.totalFiles || 0,
            completedFiles: data.completedFiles || 0,
            remainingFiles: (data.totalFiles || 0) - (data.completedFiles || 0)
          });

          // Hide spinner if processing completed
          if (data.status === 'Success' || data.status === 'Failed') {
            setShowImageProcessorSpinner(false);
            setImageProcessorChannels([]);
            console.log('[ImageProcessor RoomContentBody] Processing completed, hiding spinner');
            triggerImageProcessorRefresh?.();
          }
        }
      });

      subscription.bind('App\\Events\\ImageProcessorPhotoUpdated', (data: any) => {
        console.log('[ImageProcessor RoomContentBody] ImageProcessorPhotoUpdated event received:', data);

        if (mounted.current && data.status === 'Success') {
          // Increment completed count when individual photo finishes
          setImageProcessorCounts((prevCounts) => ({
            ...prevCounts,
            completedFiles: prevCounts.completedFiles + 1,
            remainingFiles: prevCounts.totalFiles - (prevCounts.completedFiles + 1)
          }));
        }
      });

      return { channelName, subscription };
    });

    return () => {
      connection?.unbind('state_change', onConnectionStateChange);
      subscriptions.forEach(({ channelName, subscription }) => {
        subscription.unbind('App\\Events\\ImageProcessorUpdated');
        subscription.unbind('App\\Events\\ImageProcessorPhotoUpdated');
        subscription.unsubscribe(channelName);
        console.log('[ImageProcessor RoomContentBody] Unsubscribed from channel', channelName);
      });
    };
  }, [imageProcessor, imageProcessorChannels, checkImageProcessorPending]);

  const getDamageMaterials = useCallback(async () => {
    if (mounted.current) {
      const response: any = await dispatch(getRoomDamageMaterials(roomId));
      if (response?.data) {
        setRoomDamagedMaterials(response.data);
      }
    } else {
      setRoomDamagedMaterials(undefined);
    }
  }, [roomId, dispatch]);

  const getScopeOfWorkItems = useCallback(async () => {
    if (mounted.current) {
      if (rocketScope) {
        const response: any = await dispatch(getRoomScopeOfWorkItems(roomId));
        if (response?.data) {
          setRoomScopeOfWorkItems(
            response.data.map((item) => ({
              id: item.id,
              sheetId: item.sheet_id,
              quantity: item.quantity,
              category: item.category,
              codePart1: item.code_part_1 || '',
              codePart2: item.code_part_2 || '',
              description: item.description,
              unit: item.unit,
              rate: item.rate,
            }))
          );
        }
      } else {
        setRoomScopeOfWorkItems([]);
      }
    } else {
      setRoomScopeOfWorkItems(undefined);
    }
  }, [roomId, dispatch, rocketScope]);

  // initial fetch for damage materials
  useEffect(() => {
    if (roomId) {
      (async function fetchData() {
        await getDamageMaterials();
        await getScopeOfWorkItems();
      })();
    }
  }, [roomId, getDamageMaterials, getScopeOfWorkItems]);

  return (
    <div ref={ref}>
      {imageProcessor && showImageProcessorSpinner && (
        <div className="mb-3">
          <div style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <SecondarySpinner loading />
              Images Processing... ({imageProcessorCounts.completedFiles}/{imageProcessorCounts.totalFiles} completed)
            </div>
          </div>
        </div>
      )}
      {(mergedPhotoAlbums?.length > 0 || editable) ? (
        <Galleries roomId={roomId} editable={editable} photoAlbums={mergedPhotoAlbums} setAlbumPhotos={setAlbumPhotos} />
      ) : (
        <NoPhotoPlaceholder onClickEditButton={onEditButtonClick} />
      )}
      {property?.name !== 'singlelocation' && (
        <RoomNotes
          placeholder="Add your room notes here"
          title="Room Notes"
          roomId={roomId}
          initialNotes={roomNotes}
          category={notesCategories.photo}
          hasBookmarked={hasBookmarked}
          hasFlagged={hasFlagged}
        />
      )}
      {/* temp fix for room damage materials not loaded when re-entering rooms.
        need to figure out why the room object in reducer has everything but damage_materials */}
      {roomDamagedMaterials && roomScopeOfWorkItems && (
        <DamagedMaterialsWrapper
          roomId={roomId}
          projectId={project?.id}
          locationId={location?.id}
          roomDamagedMaterials={roomDamagedMaterials || []}
          roomScopeOfWorkItems={roomScopeOfWorkItems || []}
        />
      )}

      <RoomNotes
        placeholder="Add your damaged material notes here"
        title="Damaged Material Notes"
        category={notesCategories.damage}
        initialNotes={damageNotes}
        roomId={roomId}
      />
    </div>
  );
};

RoomContentBodyContainer.defaultProps = {
  hasBookmarked: undefined,
  hasFlagged: undefined,
};

const RoomContentBodyContainerMemo = memo(RoomContentBodyContainer, areEqual);

export { RoomContentBodyContainerMemo as RoomContentBody };
